import { useMemo, useCallback, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Formik } from 'formik';
import _get from 'lodash/get';
import pick from 'lodash/pick';
import Typography from '@material-ui/core/Typography';
import NewEventSeriesForm, { newEventSeriesSchema } from '../../components/event-series/new-form';
import { createEventSeriesMutation, convertEventToSeriesQuery } from '../../apollo/event-series';
import { primaryCityQuery } from '../../apollo/cities';
import Loading from '../../components/loading';
import ErrorMessage from '../../components/error-message';
import { buildTitle } from '../../utils/layout';
import { processMutationError } from '../../utils/formik';
import config from '../../config';
import Main from '../../components/main';
import { useAppContext } from '../../contexts/app';
import LoginRequiredNotice from '../../components/login-required-notice';

const NewEventSeries = () => {
  const router = useRouter();
  const { query } = router;
  const { isLoggedIn } = useAppContext();

  const { 
    data: convertEventData,
    loading: convertEventLoading,
    error: convertEventError,
  } = useQuery(convertEventToSeriesQuery, {
    variables: {
      id: query.convertEventId,
    },
    fetchPolicy: 'network-only',
    skip: !query.convertEventId,
  });
  const convertEvent = _get(convertEventData, 'event');

  const initialValues = useMemo(() => {
    const values = {};
    if (convertEvent) {
      Object.assign(values, {
        name: convertEvent.name,
        hostType: convertEvent.hostType,
        groupId: _get(convertEvent.group, 'id', null),
        convertEventIds: [convertEvent.id],
      });
    } else {
      Object.assign(values, {
        hostType: 'User',
        convertEventIds: [],
      });
    }
    return values;
  }, [convertEvent]);

  const {
    data: primaryCityData,
    loading: primaryCityLoading,
    error: primaryCityError,
  } = useQuery(primaryCityQuery);
  const primaryCity = _get(primaryCityData, 'me.primaryCity');

  const [createEventSeries] = useMutation(createEventSeriesMutation);

  const handleSubmit = useCallback((values, actions) => {
    createEventSeries({
      variables: {
        data: {
          ...pick(values, [
            'hostType',
            'groupId',
            'name',
            'description',
          ]),
        },
        cityId: primaryCity.id,
        convertEventIds: values.convertEventIds,
      }
    }).then(({ data: { createEventSeries: eventSeries } }) => {
      window.location = `${config().universal.baseUri}/event-series/${eventSeries.id}/edit`;
    }).catch((error) => {
      processMutationError(error, actions);
    });
  }, []);

  useEffect(() => {
    if (convertEvent && convertEvent.series) {
      router.push(
        '/event-series/[id]',
        `/event-series/${convertEvent.series.id}`
      );
    }
  }, [convertEvent]);

  if (primaryCityLoading || convertEventLoading) {
    return <Loading />;
  }
  if (primaryCityError || convertEventError) {
    return (
      <ErrorMessage
        message={(primaryCityError || convertEventError).message}
        error={(primaryCityError || convertEventError)}
      />
    );
  }

  return (
    <Main>
      <Head>
        <title>{buildTitle('Create Event Series')}</title>
        <meta name="robots" content="noindex" />
      </Head>

      <Typography variant="h1">Create Event Series</Typography>

      {!isLoggedIn && (
        <LoginRequiredNotice
          returnTo={`/event-series/new${query.convertEventId ? `?convertEventId=${query.convertEventId}` : ''}`}
          action="create an event series"
        />
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={newEventSeriesSchema}
        onSubmit={handleSubmit}
      >
        {(props) => (
          <NewEventSeriesForm
            {...props}
            disabled={!isLoggedIn}
          />
        )}
      </Formik>
    </Main>
  );
};

export default NewEventSeries;
